import { React, PropTypes } from 'imports';
import DayPicker from 'react-day-picker';
import { FormattedResource, DropPanel } from '..';
import withDateLocaleUtils, { dateLocaleUtilsPropTypes } from '../withDateLocaleUtils/withDateLocaleUtils';
import { toArray, isAncestorOrSelf } from 'utils';
import Enum from 'utils/Enum';
import 'react-day-picker/lib/style.css';
import './DateInput.less';

export const DayAlignment = Enum('START', 'END');
const IS_FIREFOX = navigator.userAgent.toLowerCase().includes('firefox');

const
    datePropType = PropTypes.instanceOf(Date),
    dateRangePropType = PropTypes.shape({
        before: datePropType,
        after: datePropType
    }),
    dateOrRangePropType = PropTypes.oneOfType([datePropType, dateRangePropType]);

class DateInput extends React.PureComponent {
    static DayAlignment() {
        return DayAlignment;
    }

    static get propTypes() {
        return {
            ...dateLocaleUtilsPropTypes,

            value: datePropType,
            placeholder: PropTypes.oneOfType([FormattedResource.resourcePropType, datePropType]),
            disabled: PropTypes.oneOfType([PropTypes.bool, dateOrRangePropType, PropTypes.arrayOf(dateOrRangePropType)]),
            dayAlignment: PropTypes.oneOf(Object.values(DayAlignment)),
            clearable: PropTypes.bool,
            autofocus: PropTypes.bool,
            className: PropTypes.string,
            onChange: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            value: null,
            disabled: false,
            dayAlignment: DayAlignment.START,
            clearable: true,
            autofocus: false,
            className: ''
        };
    }

    state = {
        textValue: '',
        pending: false,
        opened: false
    }

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentDidMount() {
        if (this.props.autofocus) {
            this.refs.input.focus();
        }
    }

    componentWillReceiveProps(nextProps) {
        this.setState({ textValue: this._stringifyDate(nextProps.value), pending: false });
    }

    _tryParseDate(textValue) {
        const date = this.props.parseDate(textValue);

        if (date && this.props.disabled && typeof this.props.disabled !== 'boolean') {
            if (toArray(this.props.disabled).some(entry =>
                entry instanceof Date && +entry === +date ||
                entry.before && +entry > +date ||
                entry.after && +entry < date
            )) {
                return null;
            }
        }

        return date;
    }

    _stringifyDate(value) {
        return this.props.stringifyDate(value);
    }

    _getCurrentDate() {
        if (!this.state.pending) {
            return this.props.value;
        }

        if (!this.state.textValue && this.props.clearable) {
            return null;
        }

        return this._tryParseDate(this.state.textValue) || (this.props.clearable ? null : this.props.value);
    }

    _blurInput() {
        if (IS_FIREFOX) {
            this.refs.input.focus();
        }        

        this.refs.input.blur();
    }

    _setCurrentDate(value, blurAfter) {
        if (+value !== +this.props.value && this.props.onChange) {
            this.props.onChange(this, value);
        }

        this.setState({ textValue: this._stringifyDate(value), pending: false }, blurAfter ? this._blurInput() : null);
    }

    _onFocus = () => {
        this.setState({ opened: true });
    }

    _onTextChange = () => {
        this.setState({ textValue: this.refs.input.value, pending: true });
    }

    _onKeyDown = (event) => {
        if (event.keyCode === 13) {
            event.stopPropagation();
            event.preventDefault();
            if (this.state.pending) {
                this._setCurrentDate(this._getCurrentDate());
            }
        }
        if (event.keyCode === 27) {
            event.stopPropagation();
            event.preventDefault();
            if (this.state.pending) {
                this._setCurrentDate(this.props.value, true);
            }
        }
    }

    _onDayClick = (date) => {
        // it's 12AM local, but we use 00:00:00.000 time for days, months and stuff
        if (this.props.dayAlignment === DayAlignment.START) {
            date.setUTCHours(0);
        }
        else if (this.props.dayAlignment === DayAlignment.END) {
            date.setUTCHours(23, 59, 59, 999);
        }

        this._setCurrentDate(+date === +this.props.value && this.props.clearable ? null : date, true);
    }

    _onBlur = (event) => {
        if (
            isAncestorOrSelf(this.refs.root.refs.root, event.relatedTarget) ||
            this.refs.root.refs.popup && isAncestorOrSelf(this.refs.root.refs.popup.container, event.relatedTarget)
        ) {
            this.refs.input.focus();
            return;
        }

        if (this.state.pending) {
            this._setCurrentDate(this._getCurrentDate());
        }

        this.setState({ opened: false });
    }

    _onClearClick = (event) => {
        this._setCurrentDate(null);

        event.stopPropagation();
    }

    _onCalendarClick = () => {
        this.refs.input.focus();
    }

    render() {
        const
            { clearable, className } = this.props,
            { textValue, opened } = this.state,
            currentDate = this._getCurrentDate(),
            placeholder = this.props.placeholder !== undefined ? this.props.placeholder : this.props.getDateFormat(),
            disabled = this.props.disabled === true;

        return (
            <DropPanel
                ref="root"
                className={'date-input ' + className + (textValue ? '' : ' empty')}
                snap={[DropPanel.SnapSide.LEFT, DropPanel.SnapSide.RIGHT]}
                openMode={DropPanel.OpenMode.BUTTON}
                closeMode={null}
                tabIndex={-1}
                title={[
                    !textValue ?
                        <div key="placeholder" className="placeholder">
                            {
                                placeholder instanceof Date ?
                                    this._stringifyDate(placeholder) :
                                    placeholder ? <FormattedResource resource={placeholder} /> : null
                            }
                        </div> :
                        null,
                    <input ref="input" key="input" type="text" className="input"
                        value={textValue}
                        disabled={disabled}
                        onFocus={this._onFocus}
                        onChange={this._onTextChange}
                        onKeyDown={this._onKeyDown}
                        onBlur={this._onBlur} />

                ]}
                icon={[
                    !disabled && textValue && clearable ? <span key="times" className="fa fa-times" onClick={this._onClearClick} /> : null,
                    <span key="calendar" className="fa fa-calendar" onClick={!disabled ? this._onCalendarClick : null} />
                ]}
                opened={opened}
                onOpen={this._onCalendarClick}
                disabled={disabled}>
                <DayPicker
                    locale={this.props.locale}
                    localeUtils={this.props.localeUtils}
                    selectedDays={currentDate}
                    disabledDays={typeof this.props.disabled !== 'boolean' ? this.props.disabled : null}
                    month={currentDate}
                    initialMonth={currentDate || (placeholder instanceof Date ? placeholder : null) || new Date()}
                    onDayClick={this._onDayClick} />
            </DropPanel>
        );
    }
}

export default withDateLocaleUtils(DateInput);